/**
 * @fileoverview PrepPuffin Terms of Service registry data.
 *
 * Hand-migrated faithfully from
 * `apps/prep-web/src/portals/marketing-website/pages/TermsOfServicePage.jsx`.
 *
 * Includes the prep-only Content Ownership (#5) and AI-Generated
 * Content (#7) sections.
 */

/** @type {import('../../types.js').LegalDocument} */
export const terms = Object.freeze({
  appKey: 'prep',
  documentKey: 'terms',
  frontmatter: {
    title: 'Terms of Service',
    productName: 'PrepPuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-04-12',
    contactEmail: 'legal@preppuffin.com',
    canonicalUrl: 'https://preppuffin.com/terms',
    ogImage: 'https://preppuffin.com/og-image.jpg',
    description:
      'PrepPuffin terms of service. Review the terms governing use of our enterprise training and learning management platform.',
  },
  sections: [
    {
      id: 'agreement-to-terms',
      title: '1. Agreement to Terms',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: 'By accessing or using {productName}, you agree to be bound by these Terms of Service. If you do not agree to these terms, do not use our services. These terms apply to all users, including businesses and individuals who access {productName} for employee training and learning management.',
        },
      ],
    },
    {
      id: 'description-of-service',
      title: '2. Description of Service',
      order: 2,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides enterprise training and learning management services for multi-location retail and service businesses. Our platform enables course authoring, assessment creation, learning path management, compliance tracking, AI-assisted content generation, and integration with the ParcelPuffin ecosystem for skills, certification, and payroll connectivity.',
        },
      ],
    },
    {
      id: 'account-registration',
      title: '3. Account Registration',
      order: 3,
      blocks: [
        {
          type: 'paragraph',
          text: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the security of your account credentials and for all activity that occurs under your account. You must notify us immediately of any unauthorized access.',
        },
      ],
    },
    {
      id: 'subscription-and-billing',
      title: '4. Subscription and Billing',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} is offered on a subscription basis with three tiers: Essentials, Professional, and Enterprise. Pricing is based on employee count and selected features. You may cancel your subscription at any time. No refunds are provided for partial billing periods.',
        },
      ],
    },
    {
      id: 'content-ownership',
      title: '5. Content Ownership',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: "Training content you create or upload to {productName} remains your intellectual property. Content generated by {productName}'s AI pipeline is owned by you upon acceptance. By publishing content to the {productName} Marketplace, you grant {productName} a non-exclusive license to display and distribute that content to marketplace purchasers.",
        },
      ],
    },
    {
      id: 'acceptable-use',
      title: '6. Acceptable Use',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'You agree to use {productName} only for lawful purposes and in accordance with these terms. You may not upload harmful content, misrepresent training completion records, or use the AI content generation features to produce misleading or discriminatory training materials.',
        },
      ],
    },
    {
      id: 'ai-generated-content',
      title: '7. AI-Generated Content',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'AI-generated training content is provided as a draft for human review. {productName} does not guarantee the accuracy, completeness, or suitability of AI-generated content for any specific regulatory or compliance purpose. You are responsible for reviewing and approving all AI-generated content before publishing it to learners.',
        },
      ],
    },
    {
      id: 'data-and-privacy',
      title: '8. Data and Privacy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'Your use of {productName} is also governed by our Privacy Policy, which describes how we collect, use, and protect your data. By using our services, you consent to the data practices described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'limitation-of-liability',
      title: '9. Limitation of Liability',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} provides training management tools and AI-assisted content generation, but does not guarantee specific training outcomes, compliance status, or regulatory approval. Training program design and compliance decisions remain the responsibility of your organization. Our liability is limited to the amount you paid for the service in the preceding 12 months.',
        },
      ],
    },
    {
      id: 'termination',
      title: '10. Termination',
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may suspend or terminate your access to {productName} if you violate these terms. You may cancel your account at any time. Upon termination, your right to use the service ceases immediately, though we will retain training records as described in our Privacy Policy.',
        },
      ],
    },
    {
      id: 'changes-to-terms',
      title: '11. Changes to Terms',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'We reserve the right to modify these terms at any time. We will provide notice of material changes via email or through our platform. Continued use of {productName} after changes take effect constitutes acceptance of the revised terms.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '12. Contact Us',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about these Terms of Service, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
